import React, { useState, useEffect } from 'react';
import { NewspaperIcon, MegaphoneIcon, UsersIcon } from '@heroicons/react/24/outline';
import { NewsItem } from '../types';
import { apiService } from '../services/api';

const LatestNews: React.FC = () => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const newsData = await apiService.getNews();
        setNewsItems(newsData);
      } catch (error) {
        console.error('Error fetching news:', error);
        // Fallback to production news data
        const productionNews: NewsItem[] = [
      {
        id: '1',
        title: 'December Newsletter Available',
        content: 'Our December newsletter is now available online and in the narthex. This month features stories about our Advent journey, upcoming Christmas events, and highlights from our parish ministries.',
        date: '2024-11-30',
        author: 'Parish Office',
        category: 'newsletter'
      },
      {
        id: '2',
        title: 'Christmas Mass Schedule',
        content: 'Join us for the celebration of Our Lord\'s Nativity. Christmas Eve Masses: 4:00 PM (Children\'s Mass), 6:00 PM, and 10:00 PM (Midnight Mass). Christmas Day Mass: 10:00 AM.',
        date: '2024-11-28',
        author: 'Parish Office',
        category: 'announcement'
      },
      {
        id: '3',
        title: 'Parish School Achievement',
        content: 'Congratulations to All Saints Catholic Primary School on their recent Outstanding Ofsted rating. The school continues to provide excellent Catholic education in our community.',
        date: '2024-11-26',
        author: 'Parish Office',
        category: 'community'
      },
      {
        id: '4',
        title: 'RCIA Programme Starting',
        content: 'Beginning January 2025, we will be offering the Rite of Christian Initiation for Adults programme for those interested in joining the Catholic Church. Enquiries welcome.',
        date: '2024-11-25',
        author: 'Parish Office',
        category: 'ministry'
      }
        ];

        setNewsItems(productionNews);
      }
    };

    fetchNews();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryIcon = (category: NewsItem['category']) => {
    const iconClass = "h-5 w-5";
    switch (category) {
      case 'newsletter':
  return <NewspaperIcon className={iconClass} />;
      case 'announcement':
  return <MegaphoneIcon className={iconClass} />;
      case 'ministry':
  return <UsersIcon className={iconClass} />;
      case 'community':
        return <UsersIcon className={iconClass} />;
      default:
        return <NewspaperIcon className={iconClass} />;
    }
  };

  const getCategoryColor = (category: NewsItem['category']) => {
    const colors = {
      newsletter: 'text-blue-600 bg-blue-50',
      announcement: 'text-red-600 bg-red-50',
      ministry: 'text-purple-600 bg-purple-50',
      community: 'text-green-600 bg-green-50'
    };
    return colors[category];
  };

  return (
    <section className="py-16 bg-white" aria-labelledby="news-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 id="news-heading" className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
            Latest News & Notices
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stay informed about parish activities, announcements, and community news.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {newsItems.map((item) => (
            <article
              key={item.id}
              className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
            >
              <div className="p-6">
                <header className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-church-dark mb-2">
                      {item.title}
                    </h3>
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <time dateTime={item.date} className="mr-4">
                        {formatDate(item.date)}
                      </time>
                      <span>by {item.author}</span>
                    </div>
                  </div>
                  <div className={`flex items-center px-2 py-1 rounded-full ${getCategoryColor(item.category)}`}>
                    {getCategoryIcon(item.category)}
                    <span className="ml-1 text-xs font-medium capitalize">
                      {item.category}
                    </span>
                  </div>
                </header>

                <div className="text-gray-700 leading-relaxed">
                  <p>
                    {item.content.length > 200 
                      ? `${item.content.substring(0, 200)}...`
                      : item.content
                    }
                  </p>
                </div>

                {item.content.length > 200 && (
                  <button 
                    onClick={() => {
                      // Create a modal or expand the content
                      alert(`Full article: ${item.title}\n\n${item.content}`);
                    }}
                    className="mt-4 text-church-blue hover:text-blue-800 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-church-blue rounded px-1"
                  >
                    Read more →
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-8">
          <button 
            onClick={() => window.location.href = '/newsletters'}
            className="bg-church-gold hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-gold focus:ring-offset-2"
          >
            View All News & Updates
          </button>
        </div>
      </div>
    </section>
  );
};

export default LatestNews;